// Per-connection SpatiaLite runtime probe (Task 15). SQLite's static
// `supportsSpatialFilters` capability is `false` because spatial functions
// (MbrIntersects / BuildMbr) exist only when the loadable `mod_spatialite`
// extension is present. This probe runs `SELECT spatialite_version()` once per
// connection and caches the result; a failure (extension not loaded) leaves
// spatial filter chips hidden. Every other engine gates purely on the static
// capability and never calls this.

import { executeQueryV2 } from '../../../database-objects/services/queryExecutor';
import { createExecutionId } from '../../../../types/queryExecution';

// Per-connection memo, positive AND negative — probed once per session.
const cache = new Map<string, boolean>();

/**
 * Whether SpatiaLite is available on this SQLite connection (i.e.
 * `spatialite_version()` resolves). Memoized per connection; any error or
 * empty result → `false`. Callers should only invoke this for SQLite
 * connections.
 */
export async function probeSqliteSpatial(
  connectionId: string,
  database: string,
): Promise<boolean> {
  if (!connectionId) return false;
  const cached = cache.get(connectionId);
  if (cached !== undefined) return cached;

  let available = false;
  try {
    const response = await executeQueryV2({
      connectionId,
      database,
      sql: 'SELECT spatialite_version()',
      executionId: createExecutionId('sqlite-spatialite-probe'),
    });
    if (response.success) {
      const rs = response.resultSets?.[0];
      const row = rs?.rows?.[0] as Record<string, unknown> | undefined;
      if (rs && row) {
        const colKey = rs.columns[0]?.id ?? rs.columns[0]?.name ?? 'spatialite_version()';
        const value = row[colKey];
        available = typeof value === 'string' && value.trim().length > 0;
      }
    }
  } catch {
    available = false;
  }
  cache.set(connectionId, available);
  return available;
}

/** Synchronous read of the cached probe result (undefined = not yet probed). */
export function getCachedSqliteSpatial(connectionId: string): boolean | undefined {
  return cache.get(connectionId);
}

/**
 * E2E seam — record a probe answer for `connectionId` without running
 * `spatialite_version()`, so a spec can exercise the SpatiaLite-present
 * branch on a machine without `mod_spatialite`. Reachable only through
 * `window.__TEST_STORES__.sqliteSpatialProbe` (exposed in test builds by
 * `main.tsx`); production code never calls it.
 */
export function primeSqliteSpatialProbeForTest(connectionId: string, available: boolean): void {
  cache.set(connectionId, available);
}
